/**
 * Overview tab - "where are we right now?": headline KPIs, pacing
 * vs last year, light-month alerts and the drillable cash-flow
 * timeline. Everything forward-looking lives on the Outlook tab.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine,
  Tooltip as RechartsTooltip, Cell,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, KpiStrip, InfoTip } from "./cfoShared";
import { PaceCard } from "./OverviewExtras";
import TimelineDrill from "./TimelineDrill";
import SalesCommissioned from "./SalesCommissioned";
import SalesKpis from "./SalesKpis";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const fmtQuarter = (q) => `${q.slice(5)} ${q.slice(2, 4)}`;

const toQuarter = (ym) => `${ym.slice(0, 4)}-Q${Math.floor((+ym.slice(5, 7) - 1) / 3) + 1}`;

const fmtGbpExact = (v) => `£${Math.round(v).toLocaleString("en-GB")}`;

export default function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState("recent");
  const [grain, setGrain] = useState("month");
  const [scope, setScope] = useState("year");
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/proposals/pipeline/revenue-timeline`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the revenue timeline."))
      .finally(() => setLoading(false));
  }, []);

  const chart = useMemo(() => {
    if (!data) return [];
    const thisMonth = data.today.slice(0, 7);
    const cutoff = range === "recent"
      ? `${Number(thisMonth.slice(0, 4)) - 1}${thisMonth.slice(4)}`
      : "0000-00";
    const filtered = data.months.filter((m) => m.month >= cutoff);
    if (grain === "quarter") {
      const agg = new Map();
      for (const m of filtered) {
        const q = toQuarter(m.month);
        const a = agg.get(q) || { period: q, actual_value: 0, scheduled_value: 0 };
        a.actual_value += m.actual_value;
        a.scheduled_value += m.scheduled_value;
        agg.set(q, a);
      }
      return [...agg.values()].map((a) => ({
        ...a,
        actual_value: Math.round(a.actual_value * 100) / 100,
        scheduled_value: Math.round(a.scheduled_value * 100) / 100,
        label: fmtQuarter(a.period),
      }));
    }
    return filtered.map((m) => ({ ...m, period: m.month, label: fmtMonth(m.month) }));
  }, [data, range, grain]);

  const openDrill = (e) => {
    const p = e?.payload?.period || e?.period;
    if (p) setDrill(p);
  };

  const k = data?.kpis;
  if (loading || error) return <TabState loading={loading} error={error} testid="revenue-overview" />;

  return (
    <div className="flex flex-col gap-4" data-testid="revenue-overview">

      <div className="flex items-center justify-end" data-testid="revenue-scope-toggle">
        <div className="flex rounded-lg border border-stone-200 overflow-hidden text-[10px] font-mono bg-white">
          {[
            { id: "year", label: "Year" },
            { id: "quarter", label: "Quarter" },
            { id: "month", label: "Month" },
          ].map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setScope(o.id)}
              data-testid={`revenue-scope-${o.id}`}
              className={`px-3 py-1 transition-colors ${scope === o.id ? "bg-[#1B4F8A] text-white" : "text-stone-500 hover:bg-stone-50"}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {(() => {
        const sc = k.scopes?.[scope];
        if (!sc) return null;
        const yearLadder = (data.billed_by_year || []).map((y) =>
          `${y.year}  ${fmtGbpShort(y.value)} · ${y.count} inv${y.yoy_pct != null ? `  ${y.yoy_pct >= 0 ? "▲" : "▼"}${Math.abs(y.yoy_pct)}%${y.yoy_basis === "same_point" ? "*" : ""}` : ""}`
        ).concat(["* vs prior year at the same point"]);
        return (
          <KpiStrip cards={[
            {
              label: `Billed · ${sc.label} to date`, value: fmtGbpExact(sc.billed_to_date), tone: "text-teal-700", testid: "revenue-kpi-billed-ytd", info: "ov_billed_ytd",
              infoExtra: yearLadder,
              sub: sc.delta_pct != null
                ? `${sc.delta_pct >= 0 ? "▲" : "▼"} ${Math.abs(sc.delta_pct)}% vs ${sc.prev_label} same point (${fmtGbpExact(sc.billed_prev_same_point)})`
                : "no comparable prior period",
              subTone: sc.delta_pct != null && sc.delta_pct < 0 ? "text-rose-600" : "text-teal-600",
            },
            {
              label: `${sc.label} · committed`, value: fmtGbpExact(sc.committed), tone: "text-[#1B4F8A]", testid: "revenue-kpi-year-committed", info: "ov_year_committed",
              sub: `billed to date + ${fmtGbpShort(sc.scheduled_remainder)} scheduled to ${sc.end_label}`,
            },
            {
              label: `Projected · by ${sc.end_label}`, value: sc.projected_in_period != null ? fmtGbpExact(sc.projected_in_period) : "-", tone: "text-amber-700", testid: "revenue-kpi-projected", info: "ov_projected",
              sub: k.projected_billings != null
                ? `of ${fmtGbpShort(k.projected_billings)} pipeline value across ${(k.projected_quotes_n || 0).toLocaleString()} live quotes`
                : undefined,
            },
            {
              label: `${sc.label} total · with projected`, value: sc.total_with_projected != null ? fmtGbpExact(sc.total_with_projected) : fmtGbpExact(sc.committed), tone: "text-slate-900", testid: "revenue-kpi-year-total", info: "ov_year_total",
              sub: `committed + projected to invoice by ${sc.end_label}`,
            },
            {
              label: `Scheduled · ${sc.next_label}`, value: fmtGbpExact(sc.next_scheduled), tone: "text-stone-700", testid: "revenue-kpi-scheduled-next-year", info: "ov_scheduled_next_year",
              sub: `${(sc.next_scheduled_count || 0).toLocaleString()} instalments${sc.projected_spill != null ? ` · +${fmtGbpShort(sc.projected_spill)} projected to land after ${sc.end_label}` : ""}`,
            },
          ]} />
        );
      })()}

      {data.pacing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pacing-panel">
          <PaceCard p={data.pacing.month} testid="pacing-month" />
          <PaceCard p={data.pacing.quarter} testid="pacing-quarter" />
        </div>
      )}

      {/* Timeline chart */}
      <section data-testid="revenue-timeline-chart" className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <PanelTitle>Cash-flow timeline · billed vs scheduled £ <InfoTip id="ov_timeline" /></PanelTitle>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 text-[10px] font-mono text-stone-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-teal-600" /> billed
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#3EA8DC] opacity-60" /> scheduled
              </span>
            </div>
            <div className="flex rounded-lg border border-stone-200 overflow-hidden text-[10px] font-mono">
              {[
                { id: "month", label: "Monthly" },
                { id: "quarter", label: "Quarterly" },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setGrain(o.id)}
                  data-testid={`revenue-grain-${o.id}`}
                  className={`px-2.5 py-1 transition-colors ${grain === o.id ? "bg-[#1B4F8A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="flex rounded-lg border border-stone-200 overflow-hidden text-[10px] font-mono">
              {[
                { id: "recent", label: "Trailing 12m" },
                { id: "all", label: "Since Jan 23" },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setRange(o.id)}
                  data-testid={`revenue-range-${o.id}`}
                  className={`px-2.5 py-1 transition-colors ${range === o.id ? "bg-[#1B4F8A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#a8a29e" }}
                axisLine={false} tickLine={false}
                interval={grain === "quarter" ? 0 : range === "all" ? 3 : 1}
              />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
              <RechartsTooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(v, name) => [fmtGbpShort(v), name === "actual_value" ? "billed" : "scheduled"]}
                labelFormatter={(l, p) => {
                  const per = p?.[0]?.payload?.period;
                  if (!per) return l;
                  return per.includes("Q") ? fmtQuarter(per) : fmtMonth(per);
                }}
              />
              <ReferenceLine
                x={grain === "quarter" ? fmtQuarter(toQuarter(data.today.slice(0, 7))) : fmtMonth(data.today.slice(0, 7))}
                stroke="#1B4F8A" strokeDasharray="4 3"
                label={{ value: "today", position: "top", fontSize: 10, fill: "#1B4F8A" }}
              />
              <Bar dataKey="actual_value" stackId="rev" maxBarSize={30} onClick={openDrill}>
                {chart.map((m) => (
                  <Cell key={m.period} fill="#0D9488" radius={m.scheduled_value ? 0 : [3, 3, 0, 0]} style={{ cursor: "pointer" }} />
                ))}
              </Bar>
              <Bar dataKey="scheduled_value" stackId="rev" radius={[3, 3, 0, 0]} maxBarSize={30} onClick={openDrill}>
                {chart.map((m) => (
                  <Cell key={m.period} fill="#3EA8DC" fillOpacity={0.55} style={{ cursor: "pointer" }} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-stone-400 mt-2">
          Source: the Work in Progress list, one row per invoice instalment, from Jan 2023.
          The month is the list's Month/Year columns (DOI as fallback); instalments in months
          before today count as billed, future ones as scheduled. Click a bar for the instalments behind it.
          {data.beyond.count > 0 && ` A further ${data.beyond.count} instalments (${fmtGbpShort(data.beyond.value)}) sit beyond the chart window, out to ${fmtMonth(data.beyond.last_month)}.`}
        </p>
      </section>

      <SalesCommissioned />

      <SalesKpis />

      {drill && <TimelineDrill period={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}
